import { communicationService } from "./communicationService";

interface SmsMessage {
  id: string;
  text: string;
  direction: "inbound" | "outbound";
  timestamp: Date;
  status?: "sending" | "queued" | "sent" | "delivered" | "failed" | "undelivered";
  messageSid?: string;
  mediaUrls?: string[]; // MMS media attachments
  errorCode?: string | null; // carrier error code on a failed message
  errorMessage?: string | null; // raw error description on a failed message
}

/**
 * Fetch SMS history for a specific phone number
 * @param phoneNumber The phone number to fetch history for
 * @param limit Optional limit for number of messages to fetch (default: 50)
 */
export const getSmsHistoryForPhone = async (
  phoneNumber: string,
  limit: number = 50
): Promise<SmsMessage[]> => {
  try {
    // Fetch SMS history from backend
    const response = await communicationService.getSms({
      page: 1,
      limit,
    });

    if (!response.items || response.items.length === 0) {
      return [];
    }

    // Filter messages for this specific phone number and transform to widget format
    const messages: SmsMessage[] = response.items
      .filter((item: any) => {
        // Normalize phone numbers for comparison (remove +1, spaces, dashes, etc)
        const normalizePhone = (phone: string) => phone.replace(/[\s\-\+\(\)]/g, '');
        const itemPhone = normalizePhone(item.phoneNumber || '');
        const targetPhone = normalizePhone(phoneNumber);

        // Match last 10 digits
        const itemLast10 = itemPhone.slice(-10);
        const targetLast10 = targetPhone.slice(-10);

        return itemLast10 === targetLast10;
      })
      .map((item: any) => {
        // Pass through every carrier status the widget understands. The bubble
        // helper handles coloring; collapsing here would lose granularity
        // (e.g., 'undelivered' rendered as red can't recover from 'failed').
        const passThrough = (status?: string): SmsMessage['status'] => {
          const s = status?.toLowerCase();
          switch (s) {
            case 'sending':
            case 'queued':
            case 'sent':
            case 'delivered':
            case 'failed':
            case 'undelivered':
              return s as SmsMessage['status'];
            default:
              return undefined;
          }
        };

        return {
          id: item.id,
          messageSid: item.messageSid, // Required for realtime status_update matching
          text: item.messageContent || '',
          direction: item.messageType === 'inbound' ? 'inbound' as const : 'outbound' as const,
          timestamp: item.messageDate ? new Date(item.messageDate) : new Date(item.createdAt),
          status: passThrough(item.messageStatus),
          mediaUrls: item.mediaUrls || item.media_urls || undefined, // MMS attachments
          errorCode: item.errorCode ?? null,
          errorMessage: item.errorMessage ?? null,
        };
      })
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()); // Sort by time ascending

    return messages;
  } catch (error) {
    // Don't throw - return empty array on error
    return [];
  }
};
